import { Typography } from "@platform/design-system";
import { Box, CircularProgress, Stack } from "@mui/material";

import { toDraftSummary, type DraftSummary } from "../../draftSummary";

import { DraftRow } from "./DraftRow";

import type { CorrespondenceDraft } from "@platform/api";

export interface DraftListProps {
  drafts: CorrespondenceDraft[];
  isLoading?: boolean;
  isError?: boolean;
  onOpenThread: (threadId: string) => void;
  /** Throws a draft away. Absent hides the control. */
  onDiscard?: (draft: DraftSummary) => void;
  /** The draft currently being discarded, so only its control is disabled. */
  discardingId?: string;
}

/**
 * The drafts waiting on this RFP, in two sections.
 *
 * Split by who wrote them because the two are different work: RUMI's are
 * waiting on a decision, the analyst's are waiting on them to finish. A
 * single list would make an analyst sort that out row by row.
 *
 * An empty section is omitted rather than shown empty — a heading with a zero
 * under it is a thing to read and discount.
 */
export const DraftList = ({
  drafts,
  isLoading = false,
  isError = false,
  onOpenThread,
  onDiscard,
  discardingId,
}: DraftListProps): React.JSX.Element => {
  if (isLoading) {
    return (
      <Centered testId="draft-list-loading">
        <CircularProgress aria-label="Loading drafts" />
      </Centered>
    );
  }

  if (isError) {
    return (
      <Centered testId="draft-list-error">
        <Typography variant="body-medium">
          These drafts could not be loaded.
        </Typography>
      </Centered>
    );
  }

  const summaries = drafts.map((draft) => toDraftSummary(draft));
  const byRumi = summaries.filter((draft) => draft.byRumi);
  const mine = summaries.filter((draft) => !draft.byRumi);

  if (summaries.length === 0) {
    return (
      <Centered testId="draft-list-empty">
        <Typography variant="body-medium" color="textSecondary">
          Nothing is waiting to be sent on this RFP.
        </Typography>
      </Centered>
    );
  }

  return (
    <Box data-testid="draft-list" sx={{ flexGrow: 1, overflowY: "auto", p: 2 }}>
      <Stack spacing={0.5} sx={{ pb: 2 }}>
        <Typography variant="header-medium">Drafts</Typography>
        <Typography variant="body-medium" color="textSecondary">
          RUMI drafts waiting for your approval, and emails you started but
          haven&apos;t sent.
        </Typography>
      </Stack>

      <Section
        testId="draft-list-rumi"
        title="RUMI drafts · Awaiting approval"
        drafts={byRumi}
        onOpenThread={onOpenThread}
        onDiscard={onDiscard}
        discardingId={discardingId}
      />
      <Section
        testId="draft-list-mine"
        title="Your drafts"
        drafts={mine}
        onOpenThread={onOpenThread}
        onDiscard={onDiscard}
        discardingId={discardingId}
      />
    </Box>
  );
};

interface SectionProps {
  testId: string;
  title: string;
  drafts: DraftSummary[];
  onOpenThread: (threadId: string) => void;
  onDiscard?: (draft: DraftSummary) => void;
  discardingId?: string;
}

const Section = ({
  testId,
  title,
  drafts,
  onOpenThread,
  onDiscard,
  discardingId,
}: SectionProps): React.JSX.Element | null => {
  if (drafts.length === 0) return null;

  return (
    <Stack data-testid={testId} spacing={1} sx={{ pb: 3 }}>
      <Typography variant="header-small" color="textSecondary">
        {`${title} · ${drafts.length}`}
      </Typography>

      {drafts.map((draft) => (
        <DraftRow
          key={draft.id}
          draft={draft}
          onOpenThread={onOpenThread}
          onDiscard={onDiscard}
          isDiscarding={draft.id === discardingId}
        />
      ))}
    </Stack>
  );
};

const Centered = ({
  testId,
  children,
}: {
  testId: string;
  children: React.ReactNode;
}): React.JSX.Element => (
  <Stack
    data-testid={testId}
    spacing={2}
    sx={{
      flexGrow: 1,
      alignItems: "center",
      justifyContent: "center",
      p: 4,
      textAlign: "center",
    }}
  >
    {children}
  </Stack>
);
